import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ContentDetails } from '../types';

interface MockPlayerProps {
  content: ContentDetails;
  onBack: () => void;
}

const MockPlayer: React.FC<MockPlayerProps> = ({ content, onBack }) => {
  const navigate = useNavigate();
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(75);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);

  const playerRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const volumeRef = useRef<HTMLDivElement>(null);
  const controlsTimeoutRef = useRef<NodeJS.Timeout>();

  // Mock duration based on content runtime or default to 2 hours
  const duration = content.runtime || 120;

  // Auto-hide controls after 3 seconds of inactivity
  useEffect(() => {
    const resetControlsTimeout = () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      setShowControls(true);
      controlsTimeoutRef.current = setTimeout(() => {
        if (isPlaying) {
          setShowControls(false);
        }
      }, 3000);
    };

    resetControlsTimeout();
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [isPlaying]);

  // Mock progress simulation when playing
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isPlaying) {
      interval = setInterval(() => {
        setCurrentTime(prev => {
          const newTime = prev + 1;
          return newTime >= duration * 60 ? 0 : newTime; // Reset when reaching end
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, duration]);

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (progressRef.current) {
      const rect = progressRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const percentage = clickX / rect.width;
      setCurrentTime(percentage * duration * 60);
    }
  };

  const handleVolumeClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (volumeRef.current) {
      const rect = volumeRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const percentage = Math.max(0, Math.min(1, clickX / rect.width));
      setVolume(percentage * 100);
      setIsMuted(false);
    }
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      playerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const progressPercentage = (currentTime / (duration * 60)) * 100;
  const volumePercentage = isMuted ? 0 : volume;

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      {/* Player Container */}
      <div
        ref={playerRef}
        className={`relative w-full max-w-6xl mx-auto bg-black rounded-lg overflow-hidden ${isFullscreen ? 'fixed inset-0 z-50 max-w-none rounded-none' : 'aspect-video'}`}
        onMouseMove={() => setShowControls(true)}
        onMouseLeave={() => isPlaying && setShowControls(false)}
      >
        {/* Video Area - Black Screen */}
        <div className="absolute inset-0 flex items-center justify-center bg-black">
          {/* Black screen - no poster image */}

          {/* Play Button Overlay (when paused) */}
          {!isPlaying && (
            <div className="absolute inset-0 flex items-center justify-center">
              <button
                onClick={handlePlayPause}
                className="w-24 h-24 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-200 backdrop-blur-sm"
                aria-label="Play"
              >
                <svg className="w-12 h-12 text-white ml-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          )}

          {/* Content Info Overlay (when paused) */}
          {!isPlaying && !isFullscreen && (
            <div className="absolute bottom-20 left-6 text-white max-w-sm">
              <h1 className="text-2xl font-bold mb-2">{content.title}</h1>
              <p className="text-sm text-gray-300 mb-3 line-clamp-2">{content.synopsis}</p>
              <div className="flex items-center space-x-3 text-xs mb-8">
                <span>{content.year}</span>
                {content.runtime && <span>{content.runtime} min</span>}
                {content.contentRating && (
                  <span className="px-2 py-1 bg-gray-700 text-white text-xs font-semibold rounded">
                    {content.contentRating}
                  </span>
                )}
                {content.rating && (
                  <div className="flex items-center">
                    <svg className="w-3 h-3 text-yellow-400 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    <span>{content.rating}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Fullscreen Content Info Overlay (when paused in fullscreen) */}
          {!isPlaying && isFullscreen && (
            <div className="absolute bottom-32 left-8 text-white max-w-md">
              <h1 className="text-4xl font-bold mb-2">{content.title}</h1>
              <p className="text-lg text-gray-300 mb-4">{content.synopsis}</p>
              <div className="flex items-center space-x-4 text-sm mb-4">
                <span>{content.year}</span>
                {content.runtime && <span>{content.runtime} min</span>}
                {content.contentRating && (
                  <span className="px-2 py-1 bg-gray-700 text-white text-sm font-semibold rounded">
                    {content.contentRating}
                  </span>
                )}
                {content.rating && (
                  <div className="flex items-center">
                    <svg className="w-4 h-4 text-yellow-400 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    <span>{content.rating}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Back Button */}
        <button
          onClick={onBack}
          className={`absolute top-6 left-6 z-20 bg-black/50 hover:bg-black/75 text-white p-3 rounded-full transition-all duration-200 ${showControls ? 'opacity-100' : 'opacity-0'
            }`}
          aria-label="Go back"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* Player Controls */}
        <div
          className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0'
            } ${isFullscreen ? 'z-30' : 'z-10'}`}
        >
          <div className="p-6">
            {/* Progress Bar */}
            <div className="mb-6">
              <div
                ref={progressRef}
                className="w-full bg-gray-600/50 rounded-full h-1 cursor-pointer hover:h-2 transition-all duration-200"
                onClick={handleProgressClick}
              >
                <div
                  className="bg-red-600 h-full rounded-full relative transition-all duration-200"
                  style={{ width: `${progressPercentage}%` }}
                >
                  <div className="absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-red-600 rounded-full opacity-0 hover:opacity-100 transition-opacity duration-200"></div>
                </div>
              </div>
            </div>

            {/* Control Buttons */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-6">
                {/* Play/Pause Button */}
                <button
                  onClick={handlePlayPause}
                  className="text-white hover:text-gray-300 transition-colors duration-200"
                  aria-label={isPlaying ? 'Pause' : 'Play'}
                >
                  {isPlaying ? (
                    <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>

                {/* Volume Controls */}
                <div
                  className="flex items-center space-x-3 relative"
                  onMouseEnter={() => setShowVolumeSlider(true)}
                  onMouseLeave={() => setShowVolumeSlider(false)}
                >
                  <button
                    onClick={toggleMute}
                    className="text-white hover:text-gray-300 transition-colors duration-200"
                    aria-label={isMuted ? 'Unmute' : 'Mute'}
                  >
                    {isMuted || volume === 0 ? (
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.793L4.617 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.617l3.766-3.793a1 1 0 011.617.793zM12.293 7.293a1 1 0 011.414 0L15 8.586l1.293-1.293a1 1 0 111.414 1.414L16.414 10l1.293 1.293a1 1 0 01-1.414 1.414L15 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L13.586 10l-1.293-1.293a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    ) : volume < 50 ? (
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.793L4.617 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.617l3.766-3.793a1 1 0 011.617.793z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.793L4.617 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.617l3.766-3.793a1 1 0 011.617.793zM11.5 13.5a1 1 0 01.5-.866V7.366a1 1 0 01-1 0v6.268a1 1 0 01.5.866zm2.5 2.5a1 1 0 01.5-.866V5.366a1 1 0 01-1 0v9.268a1 1 0 01.5.866z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>

                  {/* Volume Slider */}
                  <div
                    className={`transition-all duration-200 ${showVolumeSlider ? 'w-20 opacity-100' : 'w-0 opacity-0'} overflow-hidden`}
                  >
                    <div
                      ref={volumeRef}
                      className="bg-gray-600/50 rounded-full h-1 cursor-pointer hover:h-2 transition-all duration-200"
                      onClick={handleVolumeClick}
                    >
                      <div
                        className="bg-white h-full rounded-full relative"
                        style={{ width: `${volumePercentage}%` }}
                      >
                        <div className="absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full opacity-0 hover:opacity-100 transition-opacity duration-200"></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Time Display */}
                <span className="text-white text-sm font-mono">
                  {formatTime(currentTime)} / {formatTime(duration * 60)}
                </span>
              </div>

              <div className="flex items-center space-x-4">
                {/* Settings Button */}
                <div className="relative">
                  <button
                    onClick={() => setShowSettings(!showSettings)}
                    className="text-white hover:text-gray-300 transition-colors duration-200"
                    aria-label="Settings"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </button>

                  {/* Settings Menu */}
                  {showSettings && (
                    <div className="absolute bottom-full right-0 mb-2 bg-black/90 backdrop-blur-sm rounded-lg p-4 min-w-48 z-40">
                      <div className="text-white text-sm space-y-3">
                        <div className="border-b border-gray-600 pb-2 mb-2">
                          <h3 className="font-semibold">Playback Settings</h3>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span>Quality</span>
                            <span className="text-gray-400">Auto (1080p)</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span>Speed</span>
                            <span className="text-gray-400">1x</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span>Subtitles</span>
                            <span className="text-gray-400">Off</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Fullscreen Button */}
                <button
                  onClick={toggleFullscreen}
                  className="text-white hover:text-gray-300 transition-colors duration-200"
                  aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                >
                  {isFullscreen ? (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  ) : (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Mock Loading Indicator */}
        {isPlaying && currentTime === 0 && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50">
            <div className="flex items-center space-x-3 text-white">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
              <span>Loading...</span>
            </div>
          </div>
        )}
      </div>

      {/* Content Metadata Section - Outside player container */}
      {!isFullscreen && (
        <div className="max-w-6xl mx-auto mt-4 grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content Info */}
          <div className="lg:col-span-2 space-y-6">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">{content.title}</h1>
              <div className="flex items-center space-x-4 text-gray-300 mb-8">
                <span>{content.year}</span>
                {content.runtime && <span>{content.runtime} min</span>}
                <span className="capitalize">{content.type}</span>
                {content.contentRating && (
                  <span className="px-2 py-1 bg-gray-700 text-white text-xs font-semibold rounded">
                    {content.contentRating}
                  </span>
                )}
                {content.rating && (
                  <div className="flex items-center">
                    <svg className="w-4 h-4 text-yellow-400 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    <span>{content.rating}/10</span>
                  </div>
                )}
              </div>

              {/* Genres */}
              <div className="flex flex-wrap gap-2 mb-4">
                {content.genres.map((genre) => (
                  <span key={genre} className="px-3 py-1 bg-gray-700 text-gray-300 rounded-full text-sm">
                    {genre}
                  </span>
                ))}
              </div>

              <p className="text-gray-300 leading-relaxed">{content.synopsis}</p>
            </div>

            {/* Cast & Crew */}
            <div className="space-y-4">
              {content.directors.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Directors</h3>
                  <div className="flex flex-wrap gap-2">
                    {content.directors.map((director) => (
                      <span
                        key={director.id}
                        className="text-gray-300 hover:text-white cursor-pointer transition-colors duration-200"
                        onClick={() => navigate(`/person/${director.id}`)}
                      >
                        {director.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {content.writers.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Writers</h3>
                  <div className="flex flex-wrap gap-2">
                    {content.writers.map((writer) => (
                      <span
                        key={writer.id}
                        className="text-gray-300 hover:text-white cursor-pointer transition-colors duration-200"
                        onClick={() => navigate(`/person/${writer.id}`)}
                      >
                        {writer.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {content.cast.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4">Cast</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {content.cast.slice(0, 12).map((actor) => (
                      <div
                        key={actor.id}
                        className="flex items-center space-x-3 p-3 bg-gray-800/50 rounded-lg hover:bg-gray-700/50 cursor-pointer transition-all duration-200"
                        onClick={() => navigate(`/person/${actor.id}`)}
                      >
                        {/* Actor Photo */}
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 bg-gray-700 rounded-full overflow-hidden">
                            {actor.imageUrl ? (
                              <img
                                src={actor.imageUrl}
                                alt={actor.name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                }}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs font-medium">
                                {actor.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Actor Info */}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-white truncate">{actor.name}</div>
                          {actor.character && (
                            <div className="text-sm text-gray-400 truncate">as {actor.character}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {content.cast.length > 12 && (
                    <button className="text-blue-400 hover:text-blue-300 text-sm mt-4 font-medium">
                      Show all cast ({content.cast.length})
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Poster */}
            <div className="bg-gray-800 rounded-lg p-4">
              <img
                src={content.imageUrl}
                alt={content.title}
                className="w-full rounded-lg mb-4"
              />
              <button className="w-full bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-lg font-medium transition-colors duration-200">
                Add to Watchlist
              </button>
            </div>

            {/* Additional Info */}
            <div className="bg-gray-800 rounded-lg p-4 space-y-3">
              <h3 className="text-lg font-semibold text-white">Details</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Release Year</span>
                  <span className="text-white">{content.year}</span>
                </div>
                {content.runtime && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Runtime</span>
                    <span className="text-white">{content.runtime} minutes</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-400">Type</span>
                  <span className="text-white capitalize">{content.type}</span>
                </div>
                {content.contentRating && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Content Rating</span>
                    <span className="text-white">
                      <span className="px-2 py-1 bg-gray-700 text-white text-xs font-semibold rounded">
                        {content.contentRating}
                      </span>
                    </span>
                  </div>
                )}
                {content.rating && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">IMDb Rating</span>
                    <span className="text-white">{content.rating}/10</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MockPlayer;